import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { itemAPI, demandAPI, locationAPI, attributeAPI } from '../../services/api';
import { AttributeFields, AttributeValues, toAttributePayload, prefillFromSpecs } from '../../components/AttributeFields';
import { FormSkeleton } from '../../components/skeletons';
import { useAuth } from '../../context/AuthContext';

// sessionStorage key for in-progress bids that survive a login bounce.
// sessionStorage (not localStorage) so the draft is cleared on tab close.
const BID_DRAFT_KEY = 'kanjusee_bid_draft';

// ─── Mini Media Viewer for Web Post Demand Form ────────────────────────────────
function MiniMediaViewer({ item }) {
  const allImages = Array.isArray(item.images) && item.images.length > 0
    ? item.images
    : (item.image ? [item.image] : []);
  const allVideos = Array.isArray(item.videos) ? item.videos : [];
  
  const mediaList = [
    ...allImages.map(url => ({ type: 'image', url })),
    ...allVideos.map(url => ({ type: 'video', url }))
  ];

  const [idx, setIdx] = useState(0);

  if (mediaList.length === 0) return null;

  const current = mediaList[idx];

  const next = (e) => {
    e.stopPropagation();
    setIdx(prev => (prev + 1) % mediaList.length);
  };
  const prev = (e) => {
    e.stopPropagation();
    setIdx(prev => (prev - 1 + mediaList.length) % mediaList.length);
  };

  return (
    <div style={{ width: 150, height: 110, position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {current.type === 'image' ? (
        <img src={current.url} alt={item.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      ) : (
        <video src={current.url} controls autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }} />
      )}

      {/* Index indicator */}
      {mediaList.length > 1 && (
        <div style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 10, pointerEvents: 'none' }}>
          {idx + 1}/{mediaList.length}
        </div>
      )}

      {/* Media Type indicator */}
      <div style={{ position: 'absolute', top: 4, left: 4, background: current.type === 'video' ? 'rgba(249,115,22,0.85)' : 'rgba(34,197,94,0.85)', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 4, textTransform: 'uppercase', pointerEvents: 'none' }}>
        {current.type}
      </div>

      {/* Next/Prev buttons */}
      {mediaList.length > 1 && (
        <>
          <button onClick={prev} style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'rgba(255,255,255,0.85)', cursor: 'pointer', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 'bold', padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>‹</button>
          <button onClick={next} style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'rgba(255,255,255,0.85)', cursor: 'pointer', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 'bold', padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>›</button>
        </>
      )}
    </div>
  );
}

export default function PostDemand() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemId = searchParams.get('itemId');
  const resume = searchParams.get('resume') === '1';

  // Restore a saved draft when resuming after login/register, so a guest bid
  // isn't lost. We read on mount and clear right away.
  const draft = (() => {
    if (!resume) return null;
    try {
      const raw = sessionStorage.getItem(BID_DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [item, setItem] = useState(draft?.item || null);
  const [loading, setLoading] = useState(!!itemId && !draft?.item);
  const [step, setStep] = useState(draft ? 3 : (itemId || draft?.item ? 2 : 1));
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');

  const [form, setForm] = useState(() => ({
    price: '', qty: 1, city: user?.city || '', state: user?.state || '',
    description: '', nationwide: false,
    wantsLoan: false, downPayment: '',
    ...(draft?.form || {}),
  }));
  const [posting, setPosting] = useState(false);
  const [done, setDone] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({ price: '', qty: '' });
  // Dynamic per-category specifications (schema + the buyer's chosen values).
  const [attrSchema, setAttrSchema] = useState([]);
  const [attrValues, setAttrValues] = useState(draft?.attrValues || {});

  const validateField = (name, value) => {
    let errMsg = '';
    if (name === 'price') {
      if (!value) {
        errMsg = 'Budget is required.';
      } else {
        const p = parseFloat(value);
        if (isNaN(p) || p <= 0) {
          errMsg = 'Budget must be at least 1.';
        }
      }
    } else if (name === 'qty') {
      if (!value) {
        errMsg = 'Quantity is required.';
      } else {
        const q = parseInt(value);
        if (isNaN(q) || q <= 0) {
          errMsg = 'Quantity must be at least 1.';
        }
      }
    }
    setErrors(prev => ({ ...prev, [name]: errMsg }));
    return errMsg;
  };

  // Load the spec fields for this item's category, pre-filled from the item's
  // own specs so the buyer only adjusts what actually matters to them.
  useEffect(() => {
    if (!item) { setAttrSchema([]); return undefined; }
    const categoryId = item.categoryId?._id || item.categoryId;
    const subCategoryId = item.subCategoryId?._id || item.subCategoryId;
    const brandId = item.brandId?._id || item.brandId;
    if (!categoryId) return undefined;
    let cancelled = false;
    attributeAPI.resolve({ categoryId, subCategoryId: subCategoryId || undefined, brandId: brandId || undefined })
      .then(r => {
        if (cancelled) return;
        const attrs = r.data?.data?.attributes || [];
        setAttrSchema(attrs);
        // prefill from item specs, but never clobber what the buyer already set
        setAttrValues(prev => ({ ...prefillFromSpecs(attrs, item.specs), ...prev }));
      })
      .catch(() => { if (!cancelled) setAttrSchema([]); });
    return () => { cancelled = true; };
  }, [item]);

  // After restoring the draft, clean up so refresh doesn't re-restore.
  useEffect(() => {
    if (resume) {
      sessionStorage.removeItem(BID_DRAFT_KEY);
      const next = new URLSearchParams(searchParams);
      next.delete('resume');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (itemId && !draft?.item) {
      itemAPI.get(itemId).then(r => { setItem(r.data.data); setLoading(false); }).catch(() => setLoading(false));
    }
    locationAPI.states().then(r => setStates(r.data.data || [])).catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  useEffect(() => {
    if (form.state) {
      locationAPI.cities(form.state).then(r => setCities(r.data.data || [])).catch(() => setCities([]));
    } else {
      setCities([]);
    }
  }, [form.state]);

  const searchItems = async () => {
    if (!search.trim()) return;
    const res = await itemAPI.list({ search, limit: 20 });
    setItems(res.data.data || []);
  };

  const selectItem = (i) => { setItem(i); setStep(2); };

  const persistDraftAndGoAuth = (mode) => {
    // Snapshot everything the resume path needs. Strip item populated refs
    // that are already re-fetched via itemId — we keep the lean item object
    // so the flow works even for items without a slug in the URL.
    try {
      sessionStorage.setItem(BID_DRAFT_KEY, JSON.stringify({ item, form, attrValues }));
    } catch { /* storage quota / private mode — fall through */ }
    const nextUrl = `/post-demand${item?._id ? `?itemId=${item._id}` : ''}`;
    const params = new URLSearchParams();
    params.set('next', nextUrl);
    params.set('resume', '1');
    navigate(`/${mode}?${params.toString()}`);
  };

  const handlePost = async () => {
    if (!form.price) return alert('Please enter your budget.');
    const p = parseFloat(form.price);
    if (isNaN(p) || p <= 0) return alert('Budget must be at least 1.');
    if (!item) return alert('Please pick an item first.');
    const q = parseInt(form.qty);
    if (isNaN(q) || q <= 0) return alert('Quantity must be at least 1.');

    // Guest → save draft, bounce to login. A visible "Register instead"
    // button in the review step covers the sign-up path.
    if (!user) {
      persistDraftAndGoAuth('login');
      return;
    }
    // Logged in but not a buyer — can't post demands.
    if (user.role !== 'buyer') {
      return alert('You need a buyer account to post a demand. Sign in as a buyer to continue.');
    }

    setPosting(true);
    try {
      await demandAPI.create({
        title: item.name,
        categoryId: item.categoryId?._id || item.categoryId,
        subCategoryId: item.subCategoryId?._id || item.subCategoryId || null,
        brandId: item.brandId?._id || item.brandId || null,
        price: p, qty: q,
        city: form.city, state: form.state,
        description: form.description, nationwide: form.nationwide,
        attributes: toAttributePayload(attrSchema, attrValues),
        wantsLoan: form.wantsLoan,
        downPayment: form.wantsLoan && form.downPayment ? parseFloat(form.downPayment) : null,
      });
      setDone(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to post.');
    } finally { setPosting(false); }
  };

  if (loading) return <FormSkeleton fields={5} style={{ maxWidth: 700, margin: '24px auto' }} />;

  if (done) return (
    <div style={{ textAlign: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
      <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 28, color: 'var(--green)', marginBottom: 10 }}>Demand Posted!</h2>
      <p style={{ fontSize: 14, color: 'var(--muted)', maxWidth: 360, margin: '0 auto 24px', lineHeight: 1.65 }}>Matching vendors are being notified. Expect your first offer within hours.</p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        <button className="btn btn-pri btn-md" onClick={() => navigate('/buyer/demands')}>My Demands</button>
        <button className="btn btn-out btn-md" style={{ border: '1.5px solid var(--green)' }} onClick={() => { setDone(false); setItem(null); setStep(1); }}>Post Another</button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      {/* Steps */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['Select Item', 'Name Your Price', 'Review & Post'].map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderBottom: `3px solid ${step > i + 1 ? 'var(--green)' : step === i + 1 ? 'var(--gold)' : 'var(--border)'}` }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: step > i + 1 ? 'var(--green)' : step === i + 1 ? 'var(--gold)' : 'var(--muted)' }}>{step > i + 1 ? '✓ ' : ''}{s}</span>
          </div>
        ))}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, marginBottom: 4 }}>What do you want?</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 16 }}>Search our catalog or browse all items</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input className="form-input" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchItems()} placeholder="Search iPhone, GST Filing, Tour..." style={{ flex: 1 }} />
            <button className="btn btn-pri btn-md" onClick={searchItems}>Search</button>
          </div>
          <a href="/catalog" style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, textDecoration: 'none' }}>Browse Full Catalog →</a>
          {items.length > 0 && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map(i => (
                <div key={i._id} className="card" style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => selectItem(i)}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{i.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{i.categoryId?.name}{i.subCategoryId ? ` › ${i.subCategoryId.name}` : ''}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>Select →</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && item && (
        <div>
          <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--green)', padding: '14px 18px', display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <span className="tag tag-green" style={{ fontSize: 10 }}>{item.categoryId?.name}</span>
                {item.brandId && <span className="tag" style={{ background: 'var(--gray-lt)', color: 'var(--muted)', fontSize: 10 }}>{item.brandId.name}</span>}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{item.name}</div>
              <button style={{ fontSize: 11, color: 'var(--green)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 6 }} onClick={() => { setItem(null); setStep(1); }}>← Change</button>
            </div>
            <MiniMediaViewer item={item} />
          </div>

          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, marginBottom: 16 }}>Name Your Price</h2>
          <div className="form-group">
            <label className="form-label">Your Budget ({'\u20B9'}) *</label>
            <input className="form-input" type="number" value={form.price} onChange={e => {
              const val = e.target.value;
              setForm({ ...form, price: val });
              validateField('price', val);
            }}
              onWheel={(e) => e.target.blur()} placeholder="Enter the price you're willing to pay" style={{ fontSize: 18, fontFamily: 'var(--font-serif)', fontWeight: 700, borderColor: errors.price ? '#ef4444' : undefined }} />
            {errors.price && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4, fontWeight: 500 }}>{errors.price}</div>}
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Bid what you can afford. Vendors choose whether to match it.</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Qty</label>
              <input className="form-input" type="number" min="1" value={form.qty} onChange={e => {
                const val = e.target.value;
                setForm({ ...form, qty: val });
                validateField('qty', val);
              }}
                onWheel={(e) => e.target.blur()} style={{ borderColor: errors.qty ? '#ef4444' : undefined }} />
              {errors.qty && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4, fontWeight: 500 }}>{errors.qty}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">State *</label>
              <select className="form-input" value={form.state} onChange={e => setForm({ ...form, state: e.target.value, city: '' })} style={{ cursor: 'pointer' }}>
                <option value="">Select State</option>
                {states.map(s => <option key={s.state || s} value={s.state || s}>{s.state || s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">City *</label>
              <select className="form-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} style={{ cursor: 'pointer' }} disabled={!form.state}>
                <option value="">{form.state ? 'Select City' : 'Select state first'}</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {attrSchema.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--char, #1a1a1a)' }}>Specifications</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 12px' }}>
                Tell vendors exactly what you need — precise specs get sharper quotes.
              </div>
              <AttributeFields
                attributes={attrSchema}
                values={attrValues}
                onChange={(k, v) => setAttrValues(prev => ({ ...prev, [k]: v }))}
              />
            </div>
          )}
          <div className="form-group"><label className="form-label">Anything else? (optional)</label><textarea className="form-textarea" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Delivery timeline, warranty, packaging..." rows={2} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.nationwide} onChange={e => setForm({ ...form, nationwide: e.target.checked })} />
            Accept offers from vendors outside your city (more responses, wider choice)
          </label>

          {/* Financing — sends a separate lead to banks / NBFCs only. */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 20, background: 'var(--cream, #fafaf7)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <input type="checkbox" checked={form.wantsLoan} onChange={e => setForm({ ...form, wantsLoan: e.target.checked })} />
              I want to take a loan for this
            </label>
            {form.wantsLoan && (
              <div style={{ marginTop: 10 }}>
                <label className="form-label" style={{ fontSize: 12 }}>Minimum down payment you can pay (₹) *</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  value={form.downPayment}
                  onChange={e => setForm({ ...form, downPayment: e.target.value })}
                  onWheel={(e) => e.target.blur()}
                  placeholder="e.g. 20000"
                />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                  This is sent to verified banks and NBFCs as a separate finance lead. Regular vendors will not see it.
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-out btn-md" style={{ border: '1.5px solid var(--border)' }} onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-pri btn-lg" style={{ flex: 1 }} onClick={() => {
              const errPrice = validateField('price', form.price);
              const errQty = validateField('qty', form.qty);
              if (errPrice || errQty) return;
              if (form.wantsLoan && !form.downPayment) return alert('Enter the minimum down payment you can pay.');
              setStep(3);
            }}>Review →</button>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && item && (
        <div>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, marginBottom: 16 }}>Review & Post</h2>
          <div className="card" style={{ marginBottom: 14, borderLeft: '4px solid var(--green)', padding: '14px 18px', display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <span className="tag tag-green" style={{ fontSize: 10 }}>{item.categoryId?.name}</span>
                {item.brandId && <span className="tag" style={{ background: 'var(--gray-lt)', color: 'var(--muted)', fontSize: 10 }}>{item.brandId.name}</span>}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{item.name}</div>
            </div>
            <MiniMediaViewer item={item} />
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            {(() => {
              const rows = [
                ['Item', item.name],
                ['Category', `${item.categoryId?.name || ''}${item.subCategoryId ? ` › ${item.subCategoryId.name}` : ''}${item.brandId ? ` › ${item.brandId.name}` : ''}`],
                ['Your Budget', `₹${parseFloat(form.price).toLocaleString('en-IN')}`],
                ['Qty', form.qty],
                ['Location', `${form.city}${form.state ? ', ' + form.state : ''}`],
                ['Nationwide', form.nationwide ? 'Yes' : 'No'],
                ['Loan', form.wantsLoan ? `Yes — Down payment ₹${parseFloat(form.downPayment || 0).toLocaleString('en-IN')}` : 'No'],
              ];
              return rows;
            })().map(([k, v], i, arr) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 14 }}>
                <span style={{ color: 'var(--muted)' }}>{k}</span><strong style={k === 'Your Budget' ? { color: 'var(--green)', fontFamily: 'var(--font-serif)', fontSize: 18 } : {}}>{v}</strong>
              </div>
            ))}
          </div>
          {(() => {
            const chosen = toAttributePayload(attrSchema, attrValues)
              .map(p => { const d = attrSchema.find(a => a.key === p.key); return { key: p.key, label: d?.label || p.key, value: p.value, unit: d?.unit || '' }; });
            return chosen.length ? (
              <div className="card" style={{ marginBottom: 16, padding: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Specifications</div>
                <AttributeValues items={chosen} />
              </div>
            ) : null;
          })()}
          <div style={{ background: 'var(--green-lt)', borderRadius: 10, padding: '12px 16px', marginBottom: 12, fontSize: 13, color: 'var(--green)' }}>
            💡 A realistic budget gets 3× more vendor responses. You pay nothing — vendors pay per lead.
          </div>
          {!user && (
            <div style={{ background: '#FFF8E7', border: '1px solid #EAC475', color: '#8A5A00', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.55 }}>
              You're not signed in yet — that's OK. When you post, we'll ask you to sign in or create a free account and then submit your bid automatically. Your inputs are saved until then.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-out btn-md" style={{ border: '1.5px solid var(--border)' }} onClick={() => setStep(2)}>← Edit</button>
            <button className="btn btn-pri btn-lg" style={{ flex: 1, minWidth: 200 }} onClick={handlePost} disabled={posting}>
              {posting ? 'Posting...' : user ? '🚀 Post Demand — Free' : 'Post Demand — Sign in to send →'}
            </button>
            {!user && (
              <button className="btn btn-out btn-lg" style={{ border: '1.5px solid var(--green)', color: 'var(--green)' }} onClick={() => persistDraftAndGoAuth('register')}>
                Register instead
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
